/* eslint-disable jsx-a11y/anchor-has-content */
import 'twin.macro'

import { Ring } from '@uiball/loaders'
import CheckIcon from 'assets/icons/step-done-check.svg?component'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { type ReactNode, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { getCurrentFlowType, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { type GetWsStepStatus, type GetWsStepStatuses, FLOW_STEPS } from 'share/types'
import tw, { TwStyle } from 'twin.macro'
import { getWsStepStatus, getWsStepStatuses } from 'utils'
import Emitter from 'utils/emitter'
import { v4 as uuidv4 } from 'uuid'

type OpenBorrowerAccountProps = {
  twStyle?: TwStyle
}

type StepObject = {
  status: GetWsStepStatuses | GetWsStepStatus
  apiSteps: FLOW_STEPS[]
  id: string
  title: string
  subtitle: ReactNode | string
}

const getFilialsLink: { [key: string]: string } = {
  kk: 'https://bankffin.kz/kk/branches',
  ru: 'https://bankffin.kz/ru/branches'
}

const OpenBorrowerAccount = ({ twStyle }: OpenBorrowerAccountProps) => {
  //Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const {
    OPEN_FL_CARD,
    OPEN_UL_CARD,
    OPEN_BORROWER_ACCOUNT,
    DECISION_COLLATERAL,
    CREATE_ESTIMATION,
    DECISION_LAWYER,
    CHECKING_COLLATERAL,
    AWAITING_CREDIT_MANAGER_DECISION,
    UPLOAD_BANK_DATA_IN_TELEMARKETING,
    // Third party pledger steps
    THIRD_PARTY_E_NOTARY,
    OPEN_PLEDGER_FL_CARD
  } = useAppSelector(getNotificationsSelector)
  const {
    current_step,
    borrower_record: { marital_status }
  } = useAppSelector(getStatusSelector)
  const currentFlowType = useAppSelector(getCurrentFlowType)
  // const wsData = useAppSelector(getNotificationsSelector)

  // Computed | memoized values
  const ulStepStatus = useMemo(
    () => ({
      status: getWsStepStatus(OPEN_FL_CARD),
      apiSteps: [FLOW_STEPS.OPEN_FL_CARD],
      id: uuidv4(),
      title: 'flow-open-dossier-phys',
      subtitle: 'flow-generate-doc-pack2'
    }),
    [OPEN_FL_CARD]
  )
  const flStepStatus = useMemo(() => {
    if (currentFlowType === 'THIRD_PARTY_PLEDGE') {
      return {
        status: getWsStepStatuses(OPEN_UL_CARD, OPEN_PLEDGER_FL_CARD),
        apiSteps: [FLOW_STEPS.OPEN_UL_CARD, FLOW_STEPS.OPEN_PLEDGER_FL_CARD],
        id: uuidv4(),
        title: 'flow-open-dossier-individ',
        subtitle: 'flow-generate-doc-pack2'
      }
    }
    return {
      status: getWsStepStatus(OPEN_UL_CARD),
      apiSteps: [FLOW_STEPS.OPEN_UL_CARD],
      id: uuidv4(),
      title: 'flow-open-dossier-individ',
      subtitle: 'flow-generate-doc-pack2'
    }
  }, [OPEN_PLEDGER_FL_CARD, OPEN_UL_CARD, currentFlowType])
  const openAccountStepStatus = useMemo(
    () => ({
      status: getWsStepStatuses(OPEN_BORROWER_ACCOUNT, UPLOAD_BANK_DATA_IN_TELEMARKETING),
      apiSteps: [FLOW_STEPS.OPEN_BORROWER_ACCOUNT, FLOW_STEPS.UPLOAD_BANK_DATA_IN_TELEMARKETING],
      id: uuidv4(),
      title: 'flow-open-bill',
      subtitle: 'flow-generate-doc-pack2'
    }),
    [OPEN_BORROWER_ACCOUNT, UPLOAD_BANK_DATA_IN_TELEMARKETING]
  )

  const decisionCollateralStepStatus = useMemo(() => {
    if (currentFlowType === 'COLLATERAL' && marital_status === 'MARRIED') {
      return {
        status: getWsStepStatuses(
          CHECKING_COLLATERAL,
          CREATE_ESTIMATION,
          THIRD_PARTY_E_NOTARY,
          AWAITING_CREDIT_MANAGER_DECISION,
          DECISION_LAWYER,
          DECISION_COLLATERAL
        ),
        apiSteps: [
          FLOW_STEPS.CHECKING_COLLATERAL,
          FLOW_STEPS.CREATE_ESTIMATION,
          FLOW_STEPS.THIRD_PARTY_E_NOTARY,
          FLOW_STEPS.AWAITING_CREDIT_MANAGER_DECISION,
          FLOW_STEPS.DECISION_LAWYER,
          FLOW_STEPS.DECISION_COLLATERAL
        ],
        id: uuidv4(),
        title: 'estate-check',
        subtitle: 'estate-check-sms'
      }
    }
    return {
      status: getWsStepStatuses(CHECKING_COLLATERAL, CREATE_ESTIMATION),
      apiSteps: [FLOW_STEPS.CHECKING_COLLATERAL, FLOW_STEPS.CREATE_ESTIMATION],
      id: uuidv4(),
      title: 'estate-check',
      subtitle: 'estate-check-sms'
    }
  }, [
    currentFlowType,
    marital_status,
    CHECKING_COLLATERAL,
    CREATE_ESTIMATION,
    THIRD_PARTY_E_NOTARY,
    AWAITING_CREDIT_MANAGER_DECISION,
    DECISION_LAWYER,
    DECISION_COLLATERAL
  ])

  const creditManagerStepStatus = useMemo(
    () => ({
      status: getWsStepStatuses(AWAITING_CREDIT_MANAGER_DECISION, DECISION_LAWYER, DECISION_COLLATERAL),
      apiSteps: [
        FLOW_STEPS.AWAITING_CREDIT_MANAGER_DECISION,
        FLOW_STEPS.DECISION_LAWYER,
        FLOW_STEPS.DECISION_COLLATERAL
      ],
      id: uuidv4(),
      title: 'credit-manager-check',
      subtitle: (
        <Trans
          components={{
            a: (
              <a
                href={getFilialsLink[language]}
                rel='noreferrer noopener'
                css={[tw`underline text-primary-middle font-bold`, language === 'ru' && tw`block `]}
              />
            ),
            span: <span tw='block' />
          }}
        >
          {t('credit-manager-check-desc')}
        </Trans>
      )
    }),
    [AWAITING_CREDIT_MANAGER_DECISION, DECISION_COLLATERAL, DECISION_LAWYER, language, t]
  )

  const stepsArray = useMemo<Array<StepObject>>(() => {
    if (!currentFlowType) return []
    if (currentFlowType === 'COLLATERAL' && marital_status === 'SINGLE') {
      return [decisionCollateralStepStatus, creditManagerStepStatus, ulStepStatus, flStepStatus, openAccountStepStatus]
    }
    if (currentFlowType === 'COLLATERAL' && (marital_status === 'MARRIED' || marital_status === 'DIVORCED')) {
      return [decisionCollateralStepStatus, ulStepStatus, flStepStatus, openAccountStepStatus]
    }
    // TODO: Нужен семейный статус для THIRD_PARTY_PLEDGE вместо проверки семейного положения заёмщика
    if (currentFlowType === 'THIRD_PARTY_PLEDGE' && (marital_status === 'SINGLE' || marital_status === 'DIVORCED')) {
      return [decisionCollateralStepStatus, creditManagerStepStatus, ulStepStatus, flStepStatus, openAccountStepStatus]
    }
    if (currentFlowType === 'THIRD_PARTY_PLEDGE' && marital_status === 'MARRIED') {
      return [decisionCollateralStepStatus, creditManagerStepStatus, ulStepStatus, flStepStatus, openAccountStepStatus]
    }
    return [ulStepStatus, flStepStatus, openAccountStepStatus]
  }, [
    creditManagerStepStatus,
    currentFlowType,
    decisionCollateralStepStatus,
    flStepStatus,
    marital_status,
    openAccountStepStatus,
    ulStepStatus
  ])

  const currStepIdx = useMemo(
    () =>
      stepsArray.findLastIndex(step => step.apiSteps.includes(current_step?.step as FLOW_STEPS)) ||
      stepsArray.findLastIndex(step => step.status === 'IN_PROGRESS' || step.status === 'DONE'),
    [current_step?.step, stepsArray]
  )

  useEffect(() => {
    const checkStepsArrayStatus = async () => {
      const currentSteps = stepsArray.at(currStepIdx)
      if (!currentSteps) return
      if (currentSteps.status !== 'DONE') return

      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    }

    checkStepsArrayStatus()
  }, [currStepIdx, stepsArray])

  return (
    <ContainerWithSteps>
      <InfoBlock twStyle={[twStyle]}>
        <Heading variant='title2'>{t('flow-please-wait')}!</Heading>
        <Typography variant='subbody' tw='text-grey-dark mt-2'>
          {t('flow-open-dossier')}
        </Typography>
        <ul tw='space-y-6 mt-8'>
          {stepsArray.map((step, idx) => (
            <li tw='flex items-center space-x-4' key={step.id}>
              {step.status === 'DONE' || currStepIdx > idx ? (
                <span tw='flex-shrink-0'>
                  <CheckIcon tw='w-6 h-6' />
                </span>
              ) : ['IN_PROGRESS'].includes(step.status as string) ||
                step.apiSteps.includes(current_step?.step as FLOW_STEPS) ? (
                <span tw='flex-shrink-0'>
                  <Ring color='var(--primary-color)' size={24} />
                </span>
              ) : (
                <span tw='w-6 flex-shrink-0 text-center'>
                  <Typography weight='bold'>{idx + 1}</Typography>
                </span>
              )}
              <div className='space-y-1'>
                <Typography variant='subbody' twStyle={tw`font-medium`}>
                  {t(step.title)}
                </Typography>
                <Typography variant='caption' twStyle={tw`text-grey-dark`}>
                  {typeof step.subtitle === 'string' ? t(step.subtitle) : step.subtitle}
                </Typography>
              </div>
            </li>
          ))}
        </ul>
      </InfoBlock>
    </ContainerWithSteps>
  )
}

export default OpenBorrowerAccount
